import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { CheckCircle, XCircle, Info, X } from '@phosphor-icons/react';

/**
 * The app's toast engine (queue, timers, store). Not deprecated: when it migrates, it should render
 * design-system/ui's presentational Toast inside a ToastViewport instead of its own markup.
 */
export const ToastContainer: React.FC = () => {
  let toasts: Array<{
    id: string;
    message: string;
    type?: 'success' | 'error' | 'info';
    duration?: number;
    action?: { label: string; onClick: () => void };
    description?: string;
  }> = [];
  let removeToast: (id: string) => void = () => {};

  try {
    const toastContext = useToast();
    toasts = toastContext.toasts;
    removeToast = toastContext.removeToast;
  } catch {
    // Toast context not available (SSR or not wrapped in provider)
    return null;
  }

  if (toasts.length === 0) return null;

  return (
    <div className="fixed right-4 left-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] md:bottom-6 md:right-6 md:left-auto z-(--jc-z-toast) flex flex-col gap-3 pointer-events-none items-center md:items-end">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: {
    id: string;
    message: string;
    type?: 'success' | 'error' | 'info';
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
    description?: string;
  };
  onRemove: () => void;
}

// Keep in sync with the jc-toast-out keyframe duration in components/toast.css.
const EXIT_ANIMATION_MS = 180;
// How far (px) or how fast (px/ms) a horizontal drag has to go before it counts as a dismiss
// swipe rather than a tap/scroll that snaps back.
const SWIPE_DISMISS_DISTANCE_PX = 80;
const SWIPE_DISMISS_VELOCITY = 0.5;
// Below this, a pointer move is still ambiguous (could be a tap or the start of a vertical
// page scroll) so we don't yet commit to either a horizontal drag or bailing out.
const SWIPE_INTENT_THRESHOLD_PX = 8;

const ACCENT_VAR: Record<'success' | 'error' | 'info', string> = {
  success: 'var(--jc-positive)',
  error: 'var(--jc-negative)',
  info: 'var(--jc-accent-soft)',
};

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [leaving, setLeaving] = useState(false);
  const duration = toast.duration ?? 0;
  const remainingRef = useRef(duration);
  const timerStartedAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const requestClose = useCallback(() => {
    setLeaving(true);
  }, []);

  const armTimer = useCallback(
    (ms: number) => {
      if (ms <= 0) return;
      timerStartedAtRef.current = Date.now();
      timerRef.current = setTimeout(requestClose, ms);
    },
    [requestClose],
  );

  useEffect(() => {
    armTimer(remainingRef.current);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Only ever (re)armed on mount / explicit hover pause-resume below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(onRemove, EXIT_ANIMATION_MS);
    return () => clearTimeout(t);
  }, [leaving, onRemove]);

  const pauseTimer = useCallback(() => {
    if (duration <= 0 || leaving || !timerRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    const elapsed = Date.now() - timerStartedAtRef.current;
    remainingRef.current = Math.max(remainingRef.current - elapsed, 0);
  }, [duration, leaving]);

  const resumeTimer = useCallback(() => {
    if (duration <= 0 || leaving || timerRef.current) return;
    armTimer(remainingRef.current);
  }, [duration, leaving, armTimer]);

  // Swipe-to-dismiss: tracked on the inner `.jc-toast` card, not the outer `.jc-toast-item`
  // wrapper. The wrapper carries the enter/exit `animation` (jc-toast-in/out, both fill-mode),
  // and a fill-mode:both animation keeps overriding its own `transform` property indefinitely
  // once it completes, an inline drag transform on that element would just be silently
  // ignored. The inner card has no animation of its own, so its transform is free to drive.
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStateRef = useRef<{ pointerId: number; startX: number; startY: number; active: boolean } | null>(null);
  const lastMoveRef = useRef<{ x: number; t: number } | null>(null);
  const velocityRef = useRef(0);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (leaving || (event.pointerType === 'mouse' && event.button !== 0)) return;
    dragStateRef.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, active: false };
    lastMoveRef.current = { x: event.clientX, t: performance.now() };
    velocityRef.current = 0;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    if (!state || state.pointerId !== event.pointerId) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    if (!state.active) {
      if (Math.abs(dy) > SWIPE_INTENT_THRESHOLD_PX && Math.abs(dy) >= Math.abs(dx)) {
        // Vertical intent (page scroll), let it through, stop tracking this gesture.
        dragStateRef.current = null;
        return;
      }
      if (Math.abs(dx) <= SWIPE_INTENT_THRESHOLD_PX) return;
      state.active = true;
      setDragging(true);
      pauseTimer();
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
    const now = performance.now();
    if (lastMoveRef.current) {
      const dt = now - lastMoveRef.current.t;
      if (dt > 0) velocityRef.current = (event.clientX - lastMoveRef.current.x) / dt;
    }
    lastMoveRef.current = { x: event.clientX, t: now };
    setDragX(dx);
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    dragStateRef.current = null;
    if (!state?.active) return;
    setDragging(false);
    const dx = event.clientX - state.startX;
    const dismiss = Math.abs(dx) > SWIPE_DISMISS_DISTANCE_PX || Math.abs(velocityRef.current) > SWIPE_DISMISS_VELOCITY;
    if (dismiss) {
      const cardWidth = cardRef.current?.offsetWidth ?? 320;
      setDragX(dx >= 0 ? cardWidth * 1.2 : -cardWidth * 1.2);
      requestClose();
    } else {
      setDragX(0);
      resumeTimer();
    }
  };

  const type = toast.type || 'success';
  const icon = {
    success: <CheckCircle className="h-5 w-5" weight="fill" />,
    error: <XCircle className="h-5 w-5" weight="fill" />,
    info: <Info className="h-5 w-5" weight="fill" />,
  }[type];

  const cssVars = {
    '--toast-accent': ACCENT_VAR[type],
    '--toast-duration': `${Math.max(duration, 0)}ms`,
  } as React.CSSProperties;

  const cardStyle: React.CSSProperties = {
    transform: dragX !== 0 ? `translateX(${dragX}px)` : undefined,
    opacity: dragging ? Math.max(1 - Math.abs(dragX) / 220, 0.4) : undefined,
    transition: dragging ? 'none' : 'transform 200ms ease, opacity 200ms ease',
    touchAction: 'pan-y',
  };

  return (
    <div
      className="jc-toast-item pointer-events-auto min-w-[320px] sm:min-w-[380px] max-w-lg w-full md:w-auto"
      data-leaving={leaving || undefined}
      style={cssVars}
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeTimer}
    >
      <div
        ref={cardRef}
        className={`jc-toast rounded-jc-container pl-4 pr-5 py-4 sm:pl-5 sm:pr-6 sm:py-5 flex items-start gap-4 ${dragging ? 'select-none cursor-grabbing' : ''}`}
        style={cardStyle}
        role="alert"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className="jc-toast-icon flex-shrink-0 mt-0.5 h-10 w-10 rounded-full flex items-center justify-center">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base sm:text-lg font-bold leading-tight text-jc-ink">{toast.message}</div>
          {toast.description && (
            <div className="mt-1 text-sm text-jc-ink-secondary leading-snug">{toast.description}</div>
          )}
          {toast.action && (
            <button
              onClick={() => {
                toast.action?.onClick();
                requestClose();
              }}
              className="jc-toast-action mt-3 w-full sm:w-auto px-5 py-2.5 rounded-jc-control text-sm font-semibold transition-colors active:scale-95 cursor-pointer"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        <button
          onClick={requestClose}
          className="flex-shrink-0 text-jc-ink-secondary hover:text-jc-ink transition-colors active:scale-95 mt-0.5 cursor-pointer"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        {duration > 0 && <div className="jc-toast-meniscus rounded-full" />}
      </div>
    </div>
  );
};
